import React, { useState, useEffect, useRef, useMemo } from 'react'
import { searchPitches, findPitchByLocation, pitchKind, pitchSubtitle, MAX_SUGGESTIONS } from './fieldSearch'
import { getRecentLocations } from './fieldApi'
import { SearchIcon, NoResultIcon, SuitcaseIcon, RecentIcon, OwnerPitchIllustration, RentalPitchIllustration, PlainPitchIllustration } from './FieldIcons'
import { BackButton, FlowHeading, CompactFlowHeader, SectionTitle, ListGroup, ListRow, SearchField, HelpText, Notice, LinkButton, LoadingText, visuallyHidden, scrollFieldToTop } from './FieldUI'
import { colors } from './fieldTheme'

// Choix de l'emplacement (canvas W5b ; message « Aucun résultat » repris de W5a2). Décision 17.
// Dès la première lettre, mode compact : en-tête replié, aide et lien masqués, résultats juste sous
// le champ, pour qu'ils restent visibles au-dessus du clavier (retour d'Alexandra du 24/10/2026).

export function PitchIllustration({ pitch })
{
	const kind = pitchKind(pitch)
	if (kind === 'rental')
		return <RentalPitchIllustration />
	if (kind === 'owner' || kind === 'seasonal')
		return <OwnerPitchIllustration />
	return <PlainPitchIllustration />
}

export default function FieldPitchSearch({ type, pitches, loadError, onRetry, onSelect, onStayNumber, onBack })
{
	const [query, setQuery] = useState('')
	const inputRef = useRef(null)

	useEffect(() => {
		if (inputRef.current)
			inputRef.current.focus()
	}, [])

	const results = useMemo(() => searchPitches(pitches || [], query), [pitches, query])
	const recents = useMemo(() => {
		if (!pitches)
			return []
		return getRecentLocations()
			.map(location => findPitchByLocation(pitches, location))
			.filter(Boolean)
	}, [pitches])

	const hasQuery = query.trim() !== ''
	const compact = query !== ''

	useEffect(() => {
		if (compact)
			scrollFieldToTop(inputRef.current)
	}, [compact])
	const shown = results.slice(0, MAX_SUGGESTIONS)
	const hidden = results.length - shown.length

	const clear = () => {
		setQuery('')
		if (inputRef.current)
			inputRef.current.focus()
	}

	// Entrée au clavier : on valide s'il n'y a qu'un résultat, ou une correspondance exacte
	const selectFromKeyboard = () => {
		const exact = findPitchByLocation(results, query)
		if (exact)
			onSelect(exact)
		else if (results.length === 1)
			onSelect(results[0])
	}

	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingBottom: 32 }}>
			{compact && <CompactFlowHeader type={type} backLabel="Accueil" onBack={onBack} />}
			{!compact && <BackButton label="Accueil" onClick={onBack} />}
			{!compact && <FlowHeading type={type} />}
			<label htmlFor="field-pitch" style={compact ? visuallyHidden : { display: 'block', fontSize: 17, fontWeight: 700, color: colors.text, padding: '32px 20px 12px' }}>Quel emplacement ?</label>
			{compact && <div aria-hidden="true" style={{ height: 12, flex: 'none' }} />}
			<SearchField
				id="field-pitch"
				inputRef={inputRef}
				icon={<SearchIcon />}
				value={query}
				onChange={setQuery}
				onClear={clear}
				onEnter={selectFromKeyboard}
				clearLabel="Effacer la recherche"
			/>
			{!compact && <HelpText>Lettre d'allée puis numéro. Majuscules et tirets ignorés.</HelpText>}
			{!compact && onStayNumber && (
				<div style={{ padding: '12px 8px 0' }}>
					<LinkButton icon={<SuitcaseIcon />} onClick={onStayNumber} withChevron>J'ai un numéro de séjour</LinkButton>
				</div>
			)}

			{loadError && (
				<Notice
					icon={<NoResultIcon />}
					title="Impossible de charger les emplacements"
					hint="Vérifiez la connexion, puis réessayez."
					action={<div style={{ marginLeft: -12 }}><LinkButton onClick={onRetry}>Réessayer</LinkButton></div>}
				/>
			)}
			{!loadError && !pitches && <LoadingText>Chargement des emplacements…</LoadingText>}

			{pitches && hasQuery && shown.length > 0 && (
				<React.Fragment>
					<h2 style={visuallyHidden}>Suggestions</h2>
					<div aria-hidden="true" style={{ height: 16, flex: 'none' }} />
					<ListGroup>
						{shown.map(pitch => (
							<ListRow
								key={pitch._id || pitch.location}
								icon={<PitchIllustration pitch={pitch} />}
								iconBackground="#E8EBF5"
								title={pitch.location}
								titleSize={19}
								minHeight={64}
								subtitle={pitchSubtitle(pitch)}
								onClick={() => onSelect(pitch)}
							/>
						))}
					</ListGroup>
					{hidden > 0 && <HelpText>{hidden === 1 ? '1 autre emplacement' : hidden + ' autres emplacements'} : continuez à taper pour affiner.</HelpText>}
				</React.Fragment>
			)}
			{pitches && hasQuery && shown.length === 0 && (
				<Notice icon={<NoResultIcon />} title={'Aucun emplacement pour « ' + query.trim() + ' »'} hint="Vérifiez la lettre d'allée ou le numéro." />
			)}

			{compact && onStayNumber && (
				<div style={{ padding: '16px 8px 0' }}>
					<LinkButton icon={<SuitcaseIcon />} onClick={onStayNumber} withChevron>J'ai un numéro de séjour</LinkButton>
				</div>
			)}

			{!compact && recents.length > 0 && (
				<React.Fragment>
					<SectionTitle>Récents</SectionTitle>
					<ListGroup>
						{recents.map(pitch => (
							<ListRow
								key={pitch._id || pitch.location}
								icon={<RecentIcon />}
								title={pitch.location}
								titleSize={19}
								minHeight={64}
								subtitle={pitchSubtitle(pitch)}
								onClick={() => onSelect(pitch)}
							/>
						))}
					</ListGroup>
				</React.Fragment>
			)}
		</div>
	)
}
